import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Observable} from 'rxjs';
import {AllowedEmailDto, AllowedEmailInput, UpdateUserLocalGovernmentsInput, UserDto} from './dto/users';
import {UpdateUserLegalEntityInput} from './dto/legal-entities';
import {environment} from '../../../environments/environment';
import {NbAuthService, NbAuthToken} from '@nebular/auth';
import {switchMap} from 'rxjs/operators';

@Injectable()
export class UserService {
  private baseUrl: string = environment.api.baseUrl;
  private usersByIdUrl: string = environment.api.usersById;
  private users: string = environment.api.users;
  private allowedEmails: string = environment.api.allowedEmails;

  constructor(private http: HttpClient,
              private authService: NbAuthService,
  ) {
  }

  getUser(userId: string): Observable<UserDto> {
    const url = `${this.baseUrl}${this.usersByIdUrl}${userId}`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
        });
        return this.http.get<UserDto>(url, {headers});
      }),
    );
  }

  getUsers(): Observable<UserDto[]> {
    const url = `${this.baseUrl}${this.users}`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
        });
        return this.http.get<UserDto[]>(url, {headers});
      }),
    );
  }

  getAllowedEmails(): Observable<AllowedEmailDto[]> {
    const url = `${this.baseUrl}${this.allowedEmails}`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
        });
        return this.http.get<AllowedEmailDto[]>(url, {headers});
      }),
    );
  }

  addAllowedEmail(input: AllowedEmailInput): Observable<any> {
    const url = `${this.baseUrl}${this.allowedEmails}`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
          'Content-Type': 'application/json',
        });
        return this.http.post(url, input, {headers});
      }),
    );
  }

  updateAllowedEmail(email: string, input: AllowedEmailInput): Observable<void> {
    const url = `${this.baseUrl}${this.allowedEmails}/${email}`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
          'Content-Type': 'application/json',
        });
        return this.http.put<void>(url, input, {headers});
      }),
    );
  }

  deleteAllowedEmails(email: string): Observable<any> {
    const url = `${this.baseUrl}${this.allowedEmails}/${email}`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
        });
        return this.http.delete(url, {headers});
      }),
    );
  }

  removeManagementRole(userId: string): Observable<void> {
    const url = `${this.baseUrl}${this.usersByIdUrl}${userId}/management-role`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
        });
        return this.http.delete<void>(url, {headers});
      }),
    );
  }

  updateUserRoles(userId: string, roles: string[]): Observable<void> {
    const url = `${this.baseUrl}${this.usersByIdUrl}${userId}/roles`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
          'Content-Type': 'application/json',
        });
        return this.http.put<void>(url, { roles }, { headers });
      }),
    );
  }

  updateUserCommunities(userId: string, communityIds: string[]): Observable<void> {
    const url = `${this.baseUrl}${this.usersByIdUrl}${userId}/local-governments`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
          'Content-Type': 'application/json',
        });
        return this.http.put<void>(url, { communityIds }, { headers });
      }),
    );
  }

addUserToLocalGovernment(userId: string, data: UpdateUserLocalGovernmentsInput): Observable<void> {
    const url = `${this.baseUrl}${this.usersByIdUrl}${userId}/local-governments`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
          'Content-Type': 'application/json',
        });
        return this.http.put<void>(url, data, { headers });
      }),
    );
  }

  addUserToLegalEntity(userId: string, data: UpdateUserLegalEntityInput): Observable<void> {
    const url = `${this.baseUrl}${this.usersByIdUrl}${userId}/legal-entities`;

    return this.authService.getToken().pipe(
      switchMap((token: NbAuthToken) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token.getValue()}`,
          'Content-Type': 'application/json',
        });
        return this.http.put<void>(url, data, { headers });
      }),
    );
  }
}
